import { watch } from 'vue';

import { useRoute } from 'vue-router';

import { useAccountStore } from '@/stores/account';
import { useAuthStore } from '@/stores/auth';

export const useChatwoot = () => {
  const authStore = useAuthStore();
  const accountStore = useAccountStore();
  const route = useRoute();

  let isChatwootReady = false;

  const isChatwootAvailable = () => {
    return typeof window !== 'undefined' && window.$chatwoot;
  };

  const safeChatwootCall = (callback) => {
    if (!isChatwootAvailable()) {
      console.warn('[Chatwoot] SDK not available');
      return false;
    }

    try {
      callback(window.$chatwoot);
      return true;
    } catch (error) {
      console.error('[Chatwoot] Error:', error);
      return false;
    }
  };

  const initializeChatwoot = () => {
    if (typeof window === 'undefined') return;

    // Listen for Chatwoot ready event
    window.addEventListener('chatwoot:ready', () => {
      // console.log('[Chatwoot] SDK ready')
      isChatwootReady = true;

      // Set initial context based on auth state
      // if (authStore.loggedIn) {
      //   updateAuthenticatedContext()
      // } else {
      //   updateAnonymousContext()
      // }
    });

    // If Chatwoot is already loaded, initialize immediately
    if (isChatwootAvailable()) {
      isChatwootReady = true;
      // if (authStore.loggedIn) {
      //   updateAuthenticatedContext()
      // } else {
      //   updateAnonymousContext()
      // }
    }
  };

  const updateAnonymousContext = () => {
    safeChatwootCall(($chatwoot) => {
      $chatwoot.setCustomAttributes({
        appStage: 'anonymous',
        currentPage: route.name || route.path,
        locale: 'en',
      });
      // console.log('[Chatwoot] Anonymous context updated')
    });
  };

  const updateRegistrationContext = (registrationData = {}) => {
    safeChatwootCall(($chatwoot) => {
      const { email, firstName, lastName, step } = registrationData;

      // Set minimal user info if available
      if (email) {
        const name = [firstName, lastName].filter(Boolean).join(' ') || email;
        $chatwoot.setUser(email, {
          email,
          ...(name && { name }),
        });
      }

      $chatwoot.setCustomAttributes({
        appStage: 'registration',
        registrationStep: step || 'unknown',
        currentPage: route.name || route.path,
      });
      // console.log('[Chatwoot] Registration context updated')
    });
  };

  const updateAuthenticatedContext = () => {
    if (!authStore.loggedIn) {
      console.warn('[Chatwoot] Cannot update authenticated context - user not logged in');
      return;
    }

    safeChatwootCall(($chatwoot) => {
      // Get user data from the auth store (the /users/me-shaped profile, NOT localStorage)
      const user = authStore.user;
      const businessProfile = accountStore.currentBusinessProfile;
      const marketplace = accountStore.currentMarketplace;

      if (!user || !user.sub) {
        console.warn('[Chatwoot] User data not available yet');
        return;
      }

      // Set user identification with standard Chatwoot fields
      const firstName = user.firstName || '';
      const lastName = user.lastName || '';
      const fullName = [firstName, lastName].filter(Boolean).join(' ') || user.email;

      // Build user payload with standard Chatwoot fields
      const userPayload = {
        email: user.email || '',
        name: fullName,
      };

      // Add phone number if available (use standard phone_number field)
      if (user.phone) {
        userPayload.phone_number = user.phone;
      }

      // Add company name (business profile)
      if (businessProfile?.name) {
        userPayload.company_name = businessProfile.name;
        // console.log('[Chatwoot] ✅ Company name added to payload:', businessProfile.name)
      } else {
        console.warn(
          '[Chatwoot] ⚠️ Company name NOT available - businessProfile:',
          businessProfile,
        );
      }

      // Add description with user roles from authStore.scopes (current BP); fall back to
      // the specific BP's scopes from the store if the current-BP scopes aren't loaded yet.
      let scopesArray = authStore.scopes;
      if (!scopesArray || scopesArray.length === 0) {
        scopesArray = authStore.getAppScopes(businessProfile?.id);
      }

      if (scopesArray?.length) {
        const roles = Array.isArray(scopesArray) ? scopesArray.join(', ') : scopesArray;
        userPayload.description = `Role: ${roles}`;
      }

      // Add country code from marketplace
      // if (marketplace?.countryCode) {
      //   userPayload.country_code = marketplace.countryCode
      // }

      // console.log(`[Chatwoot] Setting user with payload: ${user.sub}`, userPayload)
      $chatwoot.setUser(user.sub, userPayload);

      // Warn if business context is missing (will be updated when stores populate)
      if (!businessProfile || !marketplace) {
        console.warn(
          '[Chatwoot] Business profile or marketplace not loaded yet. Context will be incomplete.',
          {
            hasBusinessProfile: !!businessProfile,
            hasMarketplace: !!marketplace,
          },
        );
      }

      // Set custom attributes - only WisePPC-specific business context
      const customAttributes = {
        appStage: 'authenticated',
        //currentPage: route?.name || route?.path || 'N/A'
      };

      // Add WisePPC-specific business profile context
      if (businessProfile?.id) {
        customAttributes.businessProfileId = businessProfile.id.toString();
      }

      // Add company name as custom attribute (backup for standard company_name field)
      if (businessProfile?.name) {
        customAttributes.company = businessProfile.name;
        // console.log('[Chatwoot] ✅ Company added to custom attributes:', businessProfile.name)
      }

      // Add WisePPC-specific marketplace context
      if (marketplace?.profileId) {
        customAttributes.profileId = marketplace.profileId.toString();
        customAttributes.marketplaceId = marketplace.marketplaceStringId || 'N/A';
        customAttributes.sellerName = marketplace.sellerName || 'N/A';
        customAttributes.accountType = marketplace.accountType || 'N/A';
        customAttributes.syncStatus = marketplace.syncedConfiguration ? 'synced' : 'pending';
      }

      // console.log('[Chatwoot] Setting custom attributes:', customAttributes)
      $chatwoot.setCustomAttributes(customAttributes);

      // console.log('[Chatwoot] Authenticated context updated successfully', {
      //   userId: user.sub,
      //   email: user.email,
      //   companyNameInPayload: userPayload.company_name || 'NOT SET',
      //   companyInCustomAttrs: customAttributes.company || 'NOT SET',
      //   roles: scopesArray,
      //   hasPhoneNumber: !!user.phone_number,
      //   businessProfileId: businessProfile?.id,
      //   businessProfileName: businessProfile?.name,
      //   profileId: marketplace?.profileId,
      //   marketplaceId: marketplace?.marketplaceStringId,
      //   sellerName: marketplace?.sellerName,
      //   countryCode: marketplace?.countryCode
      // })
    });
  };

  const resetChatwootSession = () => {
    safeChatwootCall(($chatwoot) => {
      $chatwoot.reset();
      // console.log('[Chatwoot] Session reset')
    });
  };

  const updatePageContext = () => {
    safeChatwootCall(($chatwoot) => {
      $chatwoot.setCustomAttributes({
        currentPage: route.name || route.path,
      });
    });
  };

  const toggleChatwoot = () => {
    return safeChatwootCall(($chatwoot) => {
      $chatwoot.toggle();
    });
  };

  const setupWatchers = () => {
    // Watch for login state changes
    watch(
      () => authStore.loggedIn,
      (isLoggedIn) => {
        // console.log('[Chatwoot] Login state changed:', isLoggedIn)
        if (isLoggedIn) {
          // Only update if business profile is already loaded.
          // If not — the currentBusinessProfile watcher will handle it once data arrives.
          if (accountStore.currentBusinessProfile) {
            updateAuthenticatedContext();
          }
        } else {
          // On logout: reset session first, then set anonymous context
          resetChatwootSession();
          updateAnonymousContext();
        }
      },
    );

    // Watch for business profile changes
    watch(
      () => accountStore.currentBusinessProfile,
      (newProfile, oldProfile) => {
        // Only update if account data is fully loaded (marketplace already resolved too).
        // On initial login, marketplace fires after profile — let currentMarketplace watcher handle it.
        if (authStore.loggedIn && accountStore.isDataLoaded && newProfile?.id !== oldProfile?.id) {
          updateAuthenticatedContext();
        }
      },
      { deep: true },
    );

    // Watch for marketplace changes
    watch(
      () => accountStore.currentMarketplace,
      (newMarketplace, oldMarketplace) => {
        // Only update if logged in and marketplace actually changed
        // console.log('[Chatwoot] Marketplace changed:', newMarketplace, oldMarketplace)
        if (authStore.loggedIn && newMarketplace?.profileId !== oldMarketplace?.profileId) {
          updateAuthenticatedContext();
        }
      },
      { deep: true },
    );
  };

  return {
    initializeChatwoot,
    updateAnonymousContext,
    updateRegistrationContext,
    updateAuthenticatedContext,
    resetChatwootSession,
    updatePageContext,
    toggleChatwoot,
    setupWatchers,
    isChatwootAvailable,
  };
};
